import React, { useMemo, useState } from 'react';
import {
  Box,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useQuery } from '@apollo/client';
import { useForm } from 'react-hook-form';
import { format, startOfMonth, startOfYear, subDays, subMonths } from 'date-fns';
import moment from 'moment';
import { DateField } from 'components/form/rhf/DateField';
import { PERIOD_OPTIONS } from '../constants';
import { SubsidySummaryCards } from './SubsidySummaryCards';
import { SubsidyLedgerTable } from './SubsidyLedgerTable';
import { GET_SUBSIDY_REPORT_FILTER_VALUES } from '../api/getSubsidyReportFilterValues';
import { GET_SUBSIDY_REQUESTS_REPORT } from '../api/getSubsidyRequestsReport';

const DATE_FMT = 'yyyy-MM-dd';

function getPeriodDates(period: string): { from: string; to: string } {
  const today = new Date();
  const to = format(today, DATE_FMT);
  switch (period) {
    case 'MTD':
      return { from: format(startOfMonth(today), DATE_FMT), to };
    case 'L30':
      return { from: format(subDays(today, 30), DATE_FMT), to };
    case 'L60':
      return { from: format(subDays(today, 60), DATE_FMT), to };
    case 'YTD':
      return { from: format(startOfYear(today), DATE_FMT), to };
    case 'L12M':
      return { from: format(subMonths(today, 12), DATE_FMT), to };
    default:
      return { from: format(startOfYear(today), DATE_FMT), to };
  }
}

type FilterValues = {
  subsidyReportFilterValues: {
    lenders: { lenderId: string; lenderName: string }[];
    dealers: { dealerId: string; dealerName: string }[];
    equipments: { equipmentId: string; equipmentName: string }[];
  };
};

const ROWS_PER_PAGE = 25;

const FIXED_PERIODS = ['MTD', 'L30', 'L60', 'YTD', 'L12M'];

const isCustomPeriod = (p: string) => !FIXED_PERIODS.includes(p);

const formatCustomPeriod = (from: moment.Moment | null, to: moment.Moment | null): string => {
  const fromStr = from ? format(from.toDate(), DATE_FMT) : format(startOfYear(new Date()), DATE_FMT);
  const toStr = to ? format(to.toDate(), DATE_FMT) : format(new Date(), DATE_FMT);
  return `${fromStr} - ${toStr}`;
};

export const SubsidyReportPanel = () => {
  const [period, setPeriod] = useState<string>('YTD');
  const [selectedLender, setSelectedLender] = useState<string>('all');
  const [selectedDealer, setSelectedDealer] = useState<string>('all');
  const [selectedEquipment, setSelectedEquipment] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [sortKey] = useState<string>('customer');
  const [page, setPage] = useState<number>(0);

  const customDateForm = useForm({
    defaultValues: {
      from: moment().startOf('year'),
      to: moment(),
    },
  });

  const { data: filterData, loading: isFilterLoading } = useQuery<FilterValues>(GET_SUBSIDY_REPORT_FILTER_VALUES, {
    fetchPolicy: 'cache-and-network',
  });

  const lenders = filterData?.subsidyReportFilterValues?.lenders ?? [];
  const dealers = filterData?.subsidyReportFilterValues?.dealers ?? [];
  const equipments = filterData?.subsidyReportFilterValues?.equipments ?? [];

  const periodDates = useMemo(() => {
    let from: string;
    let to: string;
    if (isCustomPeriod(period)) {
      [from, to] = period.split(' - ');
    } else {
      ({ from, to } = getPeriodDates(period));
    }
    return {
      from: moment(from).startOf('day').toISOString(),
      to: moment(to).endOf('day').toISOString(),
    };
  }, [period]);

  const reportInput = useMemo(
    () => ({
      filters: {
        period: periodDates,
        ...(selectedLender !== 'all' && { lenderId: selectedLender }),
        ...(selectedDealer !== 'all' && { dealerId: selectedDealer }),
        ...(selectedEquipment !== 'all' && { equipmentId: selectedEquipment }),
        ...(selectedStatus !== 'all' && { status: selectedStatus }),
      },
      sorting: sortKey,
      limit: ROWS_PER_PAGE,
      offset: page * ROWS_PER_PAGE,
    }),
    [periodDates, selectedLender, selectedDealer, selectedEquipment, selectedStatus, sortKey, page]
  );

  const { data: reportData, loading: isReportLoading } = useQuery(GET_SUBSIDY_REQUESTS_REPORT, {
    variables: { input: reportInput },
    fetchPolicy: 'cache-and-network',
  });

  const report = reportData?.subsidyRequestsReport;

  return (
    <Box>
      {/* Filters bar */}
      <Stack gap={3} sx={{ mb: 2, pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
        {/* Period row */}
        <Stack direction="row" alignItems="center" gap={1} sx={{ minHeight: 40 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>
            PERIOD
          </Typography>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={isCustomPeriod(period) ? 'Custom' : period}
            onChange={(_, val) => {
              if (!val) return;
              if (val === 'Custom') {
                setPeriod(formatCustomPeriod(customDateForm.getValues('from'), customDateForm.getValues('to')));
              } else {
                setPeriod(val);
              }
            }}
          >
            {PERIOD_OPTIONS.map(p => (
              <ToggleButton key={p} value={p} sx={{ px: 1.5, py: 0.5, fontSize: '0.7rem' }}>
                {p}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          {isCustomPeriod(period) && (
            <Stack direction="row" gap={1} alignItems="center">
              <DateField
                control={customDateForm.control}
                name="from"
                label="From"
                slotProps={{ textField: { size: 'small' } }}
                extraAction={val => setPeriod(formatCustomPeriod(val, customDateForm.getValues('to')))}
              />
              <DateField
                control={customDateForm.control}
                name="to"
                label="To"
                slotProps={{ textField: { size: 'small' } }}
                extraAction={val => setPeriod(formatCustomPeriod(customDateForm.getValues('from'), val))}
              />
            </Stack>
          )}
        </Stack>

        {/* Dropdowns row */}
        <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1.5}>
          <FormControl size="small" variant="outlined" sx={{ minWidth: 130 }}>
            <InputLabel sx={{ fontSize: '0.8rem' }}>Lender</InputLabel>
            <Select
              label="Lender"
              value={selectedLender}
              onChange={e => setSelectedLender(e.target.value)}
              sx={{ fontSize: '0.8rem' }}
            >
              <MenuItem value="all">All lenders</MenuItem>
              {lenders.map(l => (
                <MenuItem key={l.lenderId} value={l.lenderId}>
                  {l.lenderName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" variant="outlined" sx={{ minWidth: 130 }}>
            <InputLabel sx={{ fontSize: '0.8rem' }}>Dealer</InputLabel>
            <Select
              label="Dealer"
              value={selectedDealer}
              onChange={e => setSelectedDealer(e.target.value)}
              sx={{ fontSize: '0.8rem' }}
            >
              <MenuItem value="all">All dealers</MenuItem>
              {dealers.map(d => (
                <MenuItem key={d.dealerId} value={d.dealerId}>
                  {d.dealerName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" variant="outlined" sx={{ minWidth: 150 }}>
            <InputLabel sx={{ fontSize: '0.8rem' }}>Equipment</InputLabel>
            <Select
              label="Equipment"
              value={selectedEquipment}
              onChange={e => setSelectedEquipment(e.target.value)}
              sx={{ fontSize: '0.8rem' }}
            >
              <MenuItem value="all">All equipment</MenuItem>
              {equipments.map(eq => (
                <MenuItem key={eq.equipmentId} value={eq.equipmentId}>
                  {eq.equipmentName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" variant="outlined" sx={{ minWidth: 110 }}>
            <InputLabel sx={{ fontSize: '0.8rem' }}>Status</InputLabel>
            <Select
              label="Status"
              value={selectedStatus}
              onChange={e => setSelectedStatus(e.target.value)}
              sx={{ fontSize: '0.8rem' }}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="contracted">Contracted</MenuItem>
              <MenuItem value="open">Open</MenuItem>
            </Select>
          </FormControl>
        </Stack>

        {/* <Stack direction="row" gap={1} ml="auto">
          <Tooltip title="Export to CSV">
            <Button size="small" variant="outlined" startIcon={<FileDownload />}>
              Export CSV
            </Button>
          </Tooltip>
          <Tooltip title="Export to PDF">
            <Button size="small" variant="outlined" startIcon={<FileDownload />}>
              Export PDF
            </Button>
          </Tooltip>
        </Stack> */}
      </Stack>

      {/* Report title */}
      <Stack direction="row" alignItems="baseline" gap={1} mb={0.5}>
        <Typography variant="h6" fontWeight={700}>
          Financing Subsidy Ledger
        </Typography>
      </Stack>
      {/* <Typography variant="caption" color="text.secondary">
        {period} · All lenders · All dealers · All equipment
      </Typography> */}

      <Divider sx={{ my: 2 }} />

      {isFilterLoading ? (
        <Stack alignItems="center" justifyContent="center" py="4rem">
          <CircularProgress />
        </Stack>
      ) : isReportLoading && !report ? (
        <Stack alignItems="center" justifyContent="center" py="4rem">
          <CircularProgress />
        </Stack>
      ) : report ? (
        <>
          <SubsidySummaryCards report={report} period={period} />
          <SubsidyLedgerTable
            deals={report.subsidyRequests ?? []}
            total={report.subsidyRequestsTotal ?? 0}
            page={page}
            rowsPerPage={ROWS_PER_PAGE}
            onPageChange={setPage}
          />
        </>
      ) : null}
    </Box>
  );
};
